import { Breakpoints, CTA, styled, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { createRef, useContext, useEffect, useState } from 'react';
import { setReturnVisitorCookie } from '../Cookies';
import { formatPostalCode, validatePostalCode } from './utils';
import { SkeletonProps } from '../styles/skeleton';
import { getTranslations } from '../translations';
import { PostalCodeInput } from './PostalCodeInput';
import { UserLocationContext } from './UserLocationContext';
import Skeleton from 'react-loading-skeleton';
import { LocationContext } from './LocationContext';

const StyledContainer = styled.div`
  padding: 24px;
  width: 100%;
  @media (min-width: ${Breakpoints.b560}px) {
    padding: 40px;
  }
  @media (min-width: ${Breakpoints.b1600}px) {
    padding: 48px;
  }
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 16px;
  > span {
    display: block;
    width: 100%;
  }
`;

const StyledCTAContainer = styled.div<SkeletonProps>`
  display: flex;
  column-gap: 16px;
  align-items: center;
`;

interface Props {
  postalCode: string;
  close: () => void;
}

const UserLocationLayer = ({postalCode, close}: Props) => {
  const locationCtx = useContext(LocationContext);
  const { postalChecked, userCloseable } = useContext(UserLocationContext);
  const inputRef = createRef<HTMLInputElement>();

  const { ulf_alt } = getTranslations();

  const [ isLoading, setIsLoading ] = useState(true);
  const [ fetchingGeolocation, setFetchingGeolocation ] = useState(false);
  const [ postalCodeValue, setPostalCodeValue ] = useState(postalCode);
  const [ hasError, setHasError ] = useState(false);

  const handleSubmit = (e: React.SyntheticEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const valid = validatePostalCode(postalCodeValue);
    if (!valid) {
      setHasError(true);
      inputRef?.current?.focus();
    } else {
      const formattedPostalCode = formatPostalCode(postalCodeValue, true);
      setPostalCodeValue(formattedPostalCode);
      locationCtx?.set(formattedPostalCode);
      setReturnVisitorCookie();
      close();
    }
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
     handleSubmit(e);
    }
  };

  const handleCancel = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    close();
  };

  useEffect(() => {
    if (postalChecked) {
      setIsLoading(false);
    }
  }, [postalChecked]);

  if (isLoading) {
    return (
      <StyledContainer>
        <Skeleton borderRadius={0} width={"40%"} height={36} />
        <Skeleton borderRadius={0} width={"80%"} height={24} />
        <Skeleton borderRadius={0} width={"60%"} height={24} />
        <Skeleton borderRadius={0} width={"40%"} height={36} />
        <Skeleton borderRadius={0} width={"40%"} height={36} />
      </StyledContainer>
    );
  };

  return (
    <StyledContainer>
      <Text appearance={TokenTextAppearance.headline300} bold>
        { ulf_alt.header }
      </Text>
      <Text appearance={TokenTextAppearance.copy200}>
        { ulf_alt.body }
      </Text>
      <PostalCodeInput
        ref={inputRef}
        postalCodeValue={postalCodeValue}
        setPostalCodeValue={setPostalCodeValue}
        handleEnter={handleEnter}
        hasError={hasError}
        setHasError={setHasError}
        fetchingGeolocation={fetchingGeolocation}
        setFetchingGeolocation={setFetchingGeolocation}
      />
      <StyledCTAContainer>
        <CTA tag="button" emphasis="primary" size="large" type="submit" onClick={handleSubmit} disabled={fetchingGeolocation}>
          { ulf_alt.cta }
        </CTA>
        {
          userCloseable
          ? <CTA tag="button" emphasis="tertiary" onClick={handleCancel}>
            { ulf_alt.cancel }
          </CTA>
          : null
        }
      </StyledCTAContainer>
    </StyledContainer>
  );
};

export default UserLocationLayer;
